/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import fecha from 'fecha';
import isNil from 'lodash/isNil';

const mappings = [
  // the year as a two-digit number (00 to 99)
  { dhtmlx: '%y', fecha: 'YY' },
  // the year as a four-digit number (1900-9999)
  { dhtmlx: '%Y', fecha: 'YYYY' },
  // the month as a number with a leading zero (01 to 12)
  { dhtmlx: '%m', fecha: 'MM' },
  // the month as a number without a leading zero (1 to 12)
  { dhtmlx: '%n', fecha: 'M' },
  // the month as an abbreviation (Jan to Dec)
  { dhtmlx: '%M', fecha: 'MMM' },
  // the month as a full name (January to December)
  { dhtmlx: '%F', fecha: 'MMMM' },
  // the day as a number with a leading zero (01 to 31)
  { dhtmlx: '%d', fecha: 'DD' },
  // the day as a number without a leading zero (1 to 31)
  { dhtmlx: '%j', fecha: 'D' },
  // the day as an abbreviation (Sun to Sat)
  { dhtmlx: '%D', fecha: 'ddd' },
  // the day as a full name (Sunday to Saturday)
  { dhtmlx: '%l', fecha: 'dddd' },
  // the hour based on the 12-hour clock (00 to 11)
  { dhtmlx: '%h', fecha: 'hh' },
  // the hour based on the 24-hour clock (00 to 23)
  { dhtmlx: '%H', fecha: 'HH' },
  // the hour based on the 12-hour clock without a leading zero (1 to 12)
  { dhtmlx: '%g', fecha: 'h' },
  // the hour based on the 24-hour clock without a leading zero (0 to 23)
  { dhtmlx: '%G', fecha: 'H' },
  // the minute as a number with a leading zero (00 to 59)
  { dhtmlx: '%i', fecha: 'mm' },
  // the second as a number with a leading zero (00 to 59)
  { dhtmlx: '%s', fecha: 'ss' },
  // displays am (from midnight until noon) and pm (from noon till midnight)
  { dhtmlx: '%a', fecha: 'a' },
  // displays AM (from midnight until noon) and PM (from noon till midnight)
  { dhtmlx: '%A', fecha: 'A' }
];

// TODO Provádět přímé parsování hodnot.
// - Nynější implementace je kompromisem mezi složitostí, funkcemi, výkonem
//   a množstvím kódu.
//   Implementace je jednoduchá, parsování obstarává knihovna.
//   Funkcionalita je pokrytá z větší části originální DHTMLX implementace,
//   ale jsou zde některé problémy (YY, %W, ...).
//   Výkon není ideální, když se provádí převod z DHTMLX do fecha formátu.
//   Množství kódu je přijatelné ve srovnání s originální implemenetací.
// - Úplně nějlepší by asi bylo vytvořit univerzální parser datumů,
//   do kterého by se v konstruktoru předal seznam pravidel (RegExp)
//   a transformaci (Function), která by vytvořila hodnoty pro daný Date.
// TODO Hodnoty z fecha formátu (např. YY, T, ..) by se neměly formátovat.
// - Měl by se provést escape hodnot, které nejsou ve formátu DHTMLX.
//   Vypadá to ale, že knihovna fecha to nepodporuje.
// - Šlo by přidat do mappings jako položky bez definovaného dhtmlx.
//   Všechny type mappings pak escapovat.
// TODO Fecha nepodporuje week number
// - %W - the ISO-8601 week number of the year. Weeks start on Monday
// - Přidat jako položku s nedefinovanou fecha?
//   Při vyhodnocení pak provést nejprve match a bez fecha vyhodit chybu?
/**
 * Načítač datumů dle formátů DXHTML.
 *
 * Zdroje:
 * - [date_format config](https://docs.dhtmlx.com/scheduler/api__scheduler_date_format_config.html)
 * - [Date Format Specification](https://docs.dhtmlx.com/scheduler/settings_format.html)
 * - [parse_date API](https://docs.dhtmlx.com/scheduler/api__scheduler_parse_date_template.html)
 */
class DhtmlxDateParser {
  /**
   * Načte datum z řetězce v konkrétním formátu.
   * @param {string} string Řetězec s konkrétním datumem
   * @param {string} format Formát řetězce s datumem dle
   * [DHTMLX specifikace](https://docs.dhtmlx.com/scheduler/settings_format.html)
   * @returns {Date} Konkrétní datum načtené z řetězce.
   */
  static parse(string, format) {
    const mappedFormat = mappings.reduce(
      (acc, mapping) => acc.replace(mapping.dhtmlx, mapping.fecha),
      format
    );
    const jsDate = fecha.parse(string, mappedFormat);
    if (isNil(jsDate)) {
      throw new Error('String does not match the format.');
    }
    return jsDate;
  }
}

export default DhtmlxDateParser;
